import React from 'react';
import type {IUseRemoteCursorTrackingProps} from '@/types/hooks/diagramHooks.types';
import {graphClientToLocal} from '@/utils/diagram/graphClientPoint';

const CURSOR_THROTTLE_MS = 80;

export function useRemoteCursorTracking({
	enabled,
	containerRef,
	modelerRef,
	modelerReady,
	viewReady,
	activeViewId,
	onCursorSend,
}: IUseRemoteCursorTrackingProps): void {
	const onCursorSendRef = React.useRef(onCursorSend);
	onCursorSendRef.current = onCursorSend;
	const activeViewIdRef = React.useRef(activeViewId);
	activeViewIdRef.current = activeViewId;
	const pendingRef = React.useRef<{x: number; y: number; viewId: string | null} | null>(null);
	const lastSentAtRef = React.useRef(0);
	const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	const sendLeave = React.useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		pendingRef.current = null;
		onCursorSendRef.current?.({left: true, viewId: activeViewIdRef.current});
	}, []);

	const flushMove = React.useCallback(() => {
		const pending = pendingRef.current;
		if (!pending) return;
		pendingRef.current = null;
		lastSentAtRef.current = Date.now();
		onCursorSendRef.current?.({
			x: pending.x,
			y: pending.y,
			viewId: pending.viewId,
			left: false,
		});
	}, []);

	React.useEffect(() => {
		if (!enabled || !modelerReady || !viewReady) return;
		const container = containerRef.current;
		if (!container) return;

		const queueMove = (clientX: number, clientY: number) => {
			const graph = modelerRef.current?.getGraph?.();
			const local = graphClientToLocal(graph, clientX, clientY);
			if (!local) return;
			pendingRef.current = {
				x: Math.round(local.x),
				y: Math.round(local.y),
				viewId: activeViewIdRef.current,
			};
			const wait = CURSOR_THROTTLE_MS - (Date.now() - lastSentAtRef.current);
			if (wait <= 0) {
				flushMove();
				return;
			}
			if (!timerRef.current) {
				timerRef.current = setTimeout(() => {
					timerRef.current = null;
					flushMove();
				}, wait);
			}
		};

		const onPointerMove = (event: PointerEvent) => {
			queueMove(event.clientX, event.clientY);
		};
		const onPointerLeave = () => sendLeave();
		const onVisibility = () => {
			if (document.visibilityState === 'hidden') sendLeave();
		};

		container.addEventListener('pointermove', onPointerMove);
		container.addEventListener('pointerleave', onPointerLeave);
		document.addEventListener('visibilitychange', onVisibility);
		window.addEventListener('pagehide', sendLeave);

		return () => {
			container.removeEventListener('pointermove', onPointerMove);
			container.removeEventListener('pointerleave', onPointerLeave);
			document.removeEventListener('visibilitychange', onVisibility);
			window.removeEventListener('pagehide', sendLeave);
			sendLeave();
		};
	}, [enabled, modelerReady, viewReady, containerRef, modelerRef, flushMove, sendLeave]);

	const skipViewLeaveRef = React.useRef(true);
	React.useEffect(() => {
		if (!enabled || !modelerReady || !viewReady) return;
		if (skipViewLeaveRef.current) {
			skipViewLeaveRef.current = false;
			return;
		}
		onCursorSendRef.current?.({left: true});
	}, [activeViewId, enabled, modelerReady, viewReady]);
}
